import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useData } from '../../context/DataContext';
import { homeworkAPI } from '../../services/api';
import PDFViewerModal from '../../components/PDFViewerModal';
import Modal from '../../components/Modal';
import './Homework.css';

const AdminHomework = () => {
  const { t } = useTranslation();
  const { courses } = useData();
  const [loading, setLoading] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [submissionsMap, setSubmissionsMap] = useState({});
  const [selectedCourse, setSelectedCourse] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    dueDate: '',
    courseId: '',
    file: null,
    isActive: true
  });

  // Modal states
  const [pdfModal, setPdfModal] = useState({ isOpen: false, url: '', title: '' });
  const [gradingModal, setGradingModal] = useState({ isOpen: false, submission: null, homeworkId: null });
  const [auditLogsModal, setAuditLogsModal] = useState({ isOpen: false, homeworkId: null, logs: [], loading: false });
  const [gradingForm, setGradingForm] = useState({ grade: '', feedback: '' });

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCourse) params.courseId = selectedCourse;
      const data = await homeworkAPI.getAll(params);
      setAssignments(data.homework || []);
    } catch (err) {
      console.error('Failed to load assignments', err);
    } finally {
      setLoading(false);
    }
  }, [selectedCourse]);

  const fetchSubmissions = async (homeworkId) => {
    try {
      const subs = await homeworkAPI.getSubmissions(homeworkId);
      setSubmissionsMap(prev => ({ ...prev, [homeworkId]: subs }));
    } catch (err) {
      console.error('Failed to load submissions', err);
    }
  };

  const fetchAuditLogs = async (homeworkId) => {
    setAuditLogsModal(prev => ({ ...prev, loading: true }));
    try {
      const logs = await homeworkAPI.getAuditLogs(homeworkId);
      setAuditLogsModal(prev => ({ ...prev, logs, loading: false }));
    } catch (err) {
      console.error('Failed to load audit logs', err);
      setAuditLogsModal(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const handleToggleActive = async (hw) => {
    try {
      await homeworkAPI.update(hw.id, { isActive: !hw.isActive });
      fetchAssignments();
    } catch (err) {
      console.error('Failed to update assignment status', err);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title || !form.courseId) return;

    setLoading(true);
    try {
      await homeworkAPI.create({
        title: form.title,
        description: form.description,
        dueDate: form.dueDate,
        courseId: form.courseId,
        isActive: form.isActive
      }, form.file);
      setForm({ title: '', description: '', dueDate: '', courseId: '', file: null, isActive: true });
      setShowCreateModal(false);
      fetchAssignments();
    } catch (err) {
      console.error('Failed to create assignment', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setForm(prev => ({ ...prev, file: e.target.files[0] }));
  };

  // PDF Modal handlers
  const openPdfModal = (url, title) => {
    setPdfModal({ isOpen: true, url, title });
  };

  const closePdfModal = () => {
    setPdfModal({ isOpen: false, url: '', title: '' });
  };

  // Grading handlers
  const openGradingModal = (submission, homeworkId) => {
    setGradingForm({ grade: submission.grade || '', feedback: submission.feedback || '' });
    setGradingModal({ isOpen: true, submission, homeworkId });
  };

  const closeGradingModal = () => {
    setGradingModal({ isOpen: false, submission: null, homeworkId: null });
    setGradingForm({ grade: '', feedback: '' });
  };

  const handleGradeSubmit = async (e) => {
    e.preventDefault();
    if (!gradingModal.submission || !gradingModal.homeworkId) return;

    const grade = parseFloat(gradingForm.grade);
    if (isNaN(grade) || grade < 0 || grade > 10) {
      alert(t('homework.gradeError'));
      return;
    }

    try {
      await homeworkAPI.gradeSubmission(
        gradingModal.homeworkId,
        gradingModal.submission.id,
        grade,
        gradingForm.feedback
      );
      closeGradingModal();
      fetchSubmissions(gradingModal.homeworkId);
    } catch (err) {
      console.error('Failed to grade submission', err);
      alert(t('common.error'));
    }
  };

  // Audit logs handlers
  const openAuditLogsModal = (homeworkId) => {
    setAuditLogsModal({ isOpen: true, homeworkId, logs: [], loading: true });
    fetchAuditLogs(homeworkId);
  };

  const closeAuditLogsModal = () => {
    setAuditLogsModal({ isOpen: false, homeworkId: null, logs: [], loading: false });
  };

  const getActionLabel = (action) => {
    const labels = {
      'download_submission': t('homework.auditDownload'),
      'view_submission': t('homework.auditView'),
      'grade_submission': t('homework.auditGrade')
    };
    return labels[action] || action;
  };

  return (
    <div className="admin-homework-page">
      <div className="page-header">
        <h1>{t('nav.homework')}</h1>
        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          {t('homework.createAssignment') || 'Create Assignment'}
        </button>
      </div>

      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title={t('homework.createAssignment') || 'Create Assignment'}>
        <form onSubmit={handleCreate}>
          <div className="form-row">
            <label>{t('course.title')}</label>
            <input
              value={form.title}
              onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
            />
          </div>
          <div className="form-row">
            <label>{t('course.description')}</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
            />
          </div>
          <div className="form-row">
            <label>{t('homework.dueDate')}</label>
            <input
              type="date"
              value={form.dueDate}
              onChange={(e) => setForm(prev => ({ ...prev, dueDate: e.target.value }))}
            />
          </div>
          <div className="form-row">
            <label>{t('course.title')}</label>
            <select
              value={form.courseId}
              onChange={(e) => setForm(prev => ({ ...prev, courseId: e.target.value }))}
            >
              <option value="">{t('common.select')}</option>
              {courses.map(c => (
                <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>{t('homework.selectFile')}</label>
            <input type="file" accept="application/pdf" onChange={handleFileChange} />
          </div>
          <div className="form-row checkbox-row">
            <label>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm(prev => ({ ...prev, isActive: e.target.checked }))}
              />
              {t('homework.activeLabel') || 'Visible to students'}
            </label>
          </div>
          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? t('common.loading') : t('homework.createAssignment') || 'Create Assignment'}
          </button>
        </form>
      </Modal>

      <section className="assignment-list">
        <h2>{t('homework.assignments') || 'Assignments'}</h2>
        <div className="filter-row">
          <label>{t('course.title')}:</label>
          <select value={selectedCourse} onChange={(e) => setSelectedCourse(e.target.value)}>
            <option value="">{t('common.all')}</option>
            {courses.map(c => (
              <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>
            ))}
          </select>
        </div>

        {loading && <div className="loading">{t('common.loading')}</div>}

        {assignments.map(hw => {
          const submissions = submissionsMap[hw.id] || [];
          return (
            <div key={hw.id} className="assignment-card">
              <div className="assignment-header">
                <div>
                  <h3>{hw.title}</h3>
                  <p className="assignment-meta">
                    {t('homework.dueDate')}: {hw.dueDate}
                    {' • '}
                    {hw.isActive ? t('homework.active') : t('homework.inactive')}
                  </p>
                </div>
                <button className="toggle-btn" onClick={() => handleToggleActive(hw)}>
                  {hw.isActive ? t('homework.deactivate') : t('homework.activate')}
                </button>
              </div>

              <div className="assignment-actions">
                {hw.attachment && (
                  <>
                    <button 
                      className="action-link"
                      onClick={() => openPdfModal(homeworkAPI.viewAttachmentUrl(hw.id), hw.title)}
                    >
                      {t('homework.viewAssignment')}
                    </button>
                    <a className="action-link" href={homeworkAPI.downloadAttachmentUrl(hw.id)} target="_blank" rel="noreferrer">
                      {t('homework.downloadAssignment')}
                    </a>
                  </>
                )}
                <a className="action-link" href={homeworkAPI.downloadSubmissionsZipUrl(hw.id)} target="_blank" rel="noreferrer">
                  {t('homework.downloadAllSubmissions')}
                </a>
                <button className="load-submissions" onClick={() => fetchSubmissions(hw.id)}>
                  {t('homework.viewSubmissions')}
                </button>
                <button className="load-submissions audit-btn" onClick={() => openAuditLogsModal(hw.id)}>
                  {t('homework.auditLogs')}
                </button>
              </div>

              {submissions.length > 0 && (
                <div className="submission-list">
                  {submissions.map(sub => (
                    <div key={sub.id} className="submission-item">
                      <div className="submission-meta">
                        <span className="submission-student">{sub.student.name}</span>
                        <span className="submission-date">{new Date(sub.submittedAt).toLocaleString()}</span>
                        {sub.grade !== undefined && sub.grade !== null && (
                          <span className={`grade-badge ${sub.grade >= 5.5 ? 'passed' : 'failed'}`}>
                            {t('grade.grade')}: {sub.grade}/10
                          </span>
                        )}
                      </div>
                      <div className="submission-actions">
                        {sub.file && (
                          <>
                            <button 
                              className="action-link"
                              onClick={() => openPdfModal(homeworkAPI.viewSubmissionUrl(hw.id, sub.id), `${sub.student.name} - ${hw.title}`)}
                            >
                              {t('homework.viewSubmission')}
                            </button>
                            <a className="action-link" href={homeworkAPI.downloadSubmissionUrl(hw.id, sub.id)} target="_blank" rel="noreferrer">
                              {t('homework.downloadSubmission')}
                            </a>
                          </>
                        )}
                        <button 
                          className="action-link grade-link"
                          onClick={() => openGradingModal(sub, hw.id)}
                        >
                          {sub.grade !== undefined && sub.grade !== null ? t('homework.updateGrade') : t('homework.gradeSubmission')}
                        </button>
                      </div>
                      {sub.feedback && (
                        <div className="submission-feedback">
                          <strong>{t('homework.feedback')}:</strong> {sub.feedback}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {!loading && assignments.length === 0 && (
          <div className="no-data">{t('common.noData')}</div>
        )}
      </section>

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={pdfModal.isOpen}
        onClose={closePdfModal}
        pdfUrl={pdfModal.url}
        title={pdfModal.title}
      />

      {/* Grading Modal */}
      {gradingModal.isOpen && (
        <div className="grading-modal-overlay" onClick={closeGradingModal}>
          <div className="grading-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="grading-modal-header">
              <h3>{t('homework.gradeSubmission')}</h3>
              <button className="grading-modal-close" onClick={closeGradingModal}>&times;</button>
            </div>
            <form onSubmit={handleGradeSubmit}>
              <div className="grading-modal-body">
                <div className="grading-student-info">
                  <strong>{gradingModal.submission?.student?.name}</strong>
                  <span>{new Date(gradingModal.submission?.submittedAt).toLocaleString()}</span>
                </div>
                <div className="form-row">
                  <label>{t('grade.grade')} (0-10)</label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    step="0.1"
                    value={gradingForm.grade}
                    onChange={(e) => setGradingForm(prev => ({ ...prev, grade: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-row">
                  <label>{t('homework.feedback')}</label>
                  <textarea
                    value={gradingForm.feedback}
                    onChange={(e) => setGradingForm(prev => ({ ...prev, feedback: e.target.value }))}
                    placeholder={t('homework.feedbackPlaceholder')}
                    rows={4}
                  />
                </div>
              </div>
              <div className="grading-modal-footer">
                <button type="button" className="cancel-btn" onClick={closeGradingModal}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="submit-btn">
                  {t('grade.addGrade')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Audit Logs Modal */}
      {auditLogsModal.isOpen && (
        <div className="audit-modal-overlay" onClick={closeAuditLogsModal}>
          <div className="audit-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="audit-modal-header">
              <h3>{t('homework.auditLogs')}</h3>
              <button className="audit-modal-close" onClick={closeAuditLogsModal}>&times;</button>
            </div>
            <div className="audit-modal-body">
              {auditLogsModal.loading ? (
                <div className="loading">{t('common.loading')}</div>
              ) : auditLogsModal.logs.length === 0 ? (
                <div className="no-data">{t('common.noData')}</div>
              ) : (
                <table className="audit-logs-table">
                  <thead>
                    <tr>
                      <th>{t('common.date')}</th>
                      <th>{t('common.name')}</th>
                      <th>{t('homework.action')}</th>
                      <th>{t('homework.target')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogsModal.logs.map(log => (
                      <tr key={log.id}>
                        <td>{new Date(log.createdAt).toLocaleString()}</td>
                        <td>{log.user?.name}</td>
                        <td>{getActionLabel(log.action)}</td>
                        <td>{log.targetType}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHomework;

